export const en = {
  tagline: 'Plan your sport around the sky',
  tabs: { today: 'Today', week: 'Week', planner: 'Planner', settings: 'Settings' },

  common: {
    close: 'Close',
    cancel: 'Cancel',
    remove: 'Remove',
    noData: 'no data',
    loading: 'Loading live forecast…',
    updated: 'updated',
    liveForecast: 'Live forecast',
    cachedForecast: 'Saved forecast',
    fetching: 'Fetching live forecast for',
    loadErr: 'Could not load forecast:',
    checkConn: 'Check your connection and reload.',
    staleBanner: 'Offline, showing the forecast saved at',
    staleBanner2: 'It may be out of date.',
    today: 'Today',
    risk: 'Risk',
  },

  risk: {
    g: 'Good to go',
    y: 'Some risk, check details',
    r: 'High risk',
    gShort: 'Good to go',
    yShort: 'Some risk',
    rShort: 'High risk',
    // one-word verdicts for the narrow block chips
    gTiny: 'Go',
    yTiny: 'Iffy',
    rTiny: 'No-go',
  },

  home: {
    hi: 'high',
    lo: 'low',
    verdictSub: 'Best window {range} · risk {score}',
    verdictBadSub: 'Scout the Week tab for a better window.',
    verdictDoneSub: 'Tomorrow is already scored on the Week tab.',
    todayBlocks: 'Today’s blocks',
    blockByBlock: 'Today, block by block',
    snowTitle: '{cm} of fresh snow on the way',
    snowBody: 'Falling over the next 48 h, {activities} will be great.',
    viewWeek: 'See the week',
    plan: 'Plan it',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    radar: 'Radar',
    cams: 'Cams',
    camsSub: 'Live views nearby',
    feelsLike: 'Feels like {t}',
    // hero narrative: weather story + block verdict fused into one line
    storyDry: 'Dry all day.',
    storyStart: '{kind} around {time}.',
    storyEnd: '{kind} until about {time}.',
    kindRain: 'Rain',
    kindSnow: 'Snow',
    bestLine: 'Best window {range} · risk {score}.',
    bestLineNone: 'No good window left today.',
    next24: 'Next 24 hours',
    bestTitle: 'Best window',
    bestNow: 'Go now, good until {end}',
    bestAt: 'Go at {range}',
    bestNone: 'No good window left today. The week below has better ones.',
    next7: 'Next 7 days',
    next7Fine: 'low → high, shared scale · dot = now',
    // captions for the other daily metrics; {u} is the display unit
    next7FineWind: 'sustained → gusts, {u} · shared scale',
    next7FineRain: 'chance of precipitation · total in {u}',
    next7FineSnow: 'snowfall total, {u} · shared scale',
    metricTemp: 'Temperature',
    show14: 'Show 14 days',
    show7: 'Show 7 days',
    dayBest: 'best block risk {score}',
    nowcastRainStart: 'Rain starting around {time}',
    nowcastSnowStart: 'Snow starting around {time}',
    nowcastRainEnd: 'Rain now, ending around {time}',
    nowcastSnowEnd: 'Snow now, ending around {time}',
    nowcastRainOn: 'Rain for the next few hours',
    nowcastSnowOn: 'Snow for the next few hours',
    nowcastRainTotal: '≈ {amt} over the next 3 h',
    nowcastSnowTotal: '≈ {amt} of snow over the next 3 h',
    radarWet: 'Precipitation nearby',
    radarDry: 'Nothing nearby',
    tileUv: 'UV index',
    tileWind: 'Wind',
    tileHumidity: 'Humidity',
    tileSun: 'Daylight',
    uv0: 'Low',
    uv1: 'Moderate',
    uv2: 'High',
    uv3: 'Very high',
    uv4: 'Extreme',
    uvAdvice: 'Protection until {time}',
    uvNone: 'No protection needed',
    gustsTo: 'Gusts to {v} around {time}',
    calmDay: 'Calm the rest of the day',
    dew0: 'Dry air',
    dew1: 'Comfortable',
    dew2: 'Humid',
    dew3: 'Muggy',
    dewPoint: 'Dew point {t}',
    daylightLeft: '{h} h of daylight left',
    tileAqhi: 'Air quality',
    aqhi0: 'Low risk',
    aqhi1: 'Moderate risk',
    aqhi2: 'High risk',
    aqhi3: 'Very high risk',
    aqhiSub0: 'AQHI: ideal air for being outdoors',
    aqhiSub1: 'AQHI: sensitive groups, take it easier',
    aqhiSub2: 'AQHI: reduce strenuous outdoor activity',
    aqhiSub3: 'AQHI: avoid strenuous outdoor activity',
    aqi0: 'Good',
    aqi1: 'Moderate',
    aqi2: 'Unhealthy for sensitive groups',
    aqi3: 'Unhealthy',
    aqi4: 'Very unhealthy',
    aqi5: 'Hazardous',
    aqiSub0: 'AQI: ideal air for being outdoors',
    aqiSub1: 'AQI: sensitive groups, take it easier',
    aqiSub2: 'AQI: reduce strenuous outdoor activity',
    aqiSub3: 'AQI: avoid strenuous outdoor activity',
    alertUntil: 'until {time}',
  },

  radar: {
    title: 'Radar & next hours',
    modeRadar: 'Radar',
    modeRain: 'Rain',
    modeWind: 'Wind',
    modeWaves: 'Waves',
    hintRadar:
      'Observed radar for the past hour, the official ECCC projection for the next, then HRDPS model rain (marked ≈) out to 6 hours. Covers Canada and the United States.',
    hintRadarGlobal:
      'Precipitation for the past hour and the next 4, one continuous nowcast by Rainbow AI. Press play or scrub the timeline at the bottom.',
    hintRain:
      'Model rain animation for the hours ahead. Press play or scrub the timeline at the bottom.',
    hintWind:
      'Wind speed and direction for the hours ahead. Same timeline, animated as flow.',
    hintWaves:
      'Swell height and direction for the hours ahead, the surfer’s and sailor’s view. Open water only.',
    now: 'now',
    play: 'Play',
    pause: 'Pause',
    hybridLoading: 'Loading radar…',
    hybridErr: 'Radar is unavailable right now. Try the Rain view.',
    noCoverage: 'Radar is not available for this area yet. Use the Rain view instead.',
    frames: 'Loading frames… {done}/{total}',
  },

  cams: {
    title: 'Nearby cams',
    loading: 'Finding cams nearby…',
    none: 'No webcams within 60 km of this spot.',
    unavailable: 'Webcams are unavailable right now.',
    live: 'LIVE',
    viewOnWindy: 'View on Windy.com',
    credit: 'Webcams by',
  },

  controls: {
    activity: 'Activity',
    chooseAct: 'Choose your activity',
    blockLen: 'Block length',
    riskTol: 'Risk tolerance',
    hours: 'Hours shown',
    cautious: 'Cautious',
    balanced: 'Balanced',
    tolerant: 'Tolerant',
    tune: 'Tune criteria',
    hourUnit: 'h',
    offSeason: 'off-season',
    addAct: 'Add activity',
  },

  add: {
    title: 'New activity',
    name: 'Name',
    namePh: 'e.g. Kayaking',
    emoji: 'Emoji',
    cat: 'Category',
    newCat: '+ New category…',
    newCatPh: 'Category name',
    season: 'Season',
    seasonWarm: 'Warm season',
    seasonWinter: 'Winter',
    seasonAll: 'Year-round',
    create: 'Add & tune criteria',
    remove: 'Delete this activity',
  },

  tune: {
    critFor: 'Criteria for',
    hint: 'How much should each factor matter? 0 = ignore, 10 = critical. Presets fill these in; adjust to make it yours. Wind and swell also take an ideal range: below the min is a risk too (surf needs waves, sailing needs wind).',
    ideal: 'ideal',
    min: 'min',
    max: 'max',
    rain: 'Rain',
    rainS: 'Chance & amount of precipitation',
    snow: 'Snow base',
    snowS: 'Risk when snow cover is too thin',
    fresh: 'Fresh snow',
    freshS: 'Wants fresh powder from the last 48 h',
    wind: 'Wind',
    windS: 'Sustained wind & gusts',
    cold: 'Cold',
    coldS: 'Too cold below',
    heat: 'Heat',
    heatS: 'Too hot above',
    uv: 'UV',
    uvS: 'Sun-exposure index',
    swell: 'Swell',
    swellS: 'Ocean swell height',
    tide: 'Tide',
    tideS: 'Risk when the tide runs high',
    reset: 'Reset to preset defaults',
  },

  board: {
    weekGlance: '· your week at a glance',
    weekGlanceSolo: 'Your week at a glance',
    legendG: 'Good to go',
    legendY: 'Some risk',
    legendR: 'High risk',
    emptyTitle: 'No forecast yet',
    nextWeek: '+ Next week',
    backWeek: '− One week',
    lowConf: 'days 8–14 are a longer-range outlook with lower confidence',
  },

  detail: {
    title: 'Block detail',
    empty: 'Tap a block in the heat map to inspect it and add it to your planner.',
    addPlanner: 'Add to my planner',
    inPlanner: 'In planner',
    rain: 'Rain',
    snow: 'Snow',
    freshSnow: 'Fresh',
    wind: 'Wind',
    feels: 'Feels like',
    uv: 'UV',
    swell: 'Swell',
    tide: 'Tide',
    tideLow: 'low',
    tideMid: 'mid',
    tideHigh: 'high',
    uvIdx: 'index',
    base: 'base',
    fresh48h: 'fresh/48 h',
    gusts: 'gusts',
    chance: 'chance',
    felt: 'felt',
    air: 'temperature',
    min: 'min',
    max: 'max',
    hourly: 'Hour by hour',
    tempCurve: 'Feels like',
    rainCurve: 'Rain',
    windCurve: 'Wind',
    mostly: 'mostly {factor}',
    scoredFor: 'Scored for {activity} with your criteria',
    adjust: 'Adjust',
    share: 'Share',
    shareText: '{activity} in {place}, {when}: {verdict}.',
    shareCopied: 'Link copied',
  },

  planner: {
    title: 'My planner',
    empty: 'No sessions planned yet. Pick a green block!',
    addCal: 'Add to calendar',
    addAllCal: 'Add all to calendar',
    calOpenEach: 'Click each session to add it',
    calBack: '← Back',
    calGoogle: 'Google Calendar',
    calOutlook: 'Outlook.com',
    calOffice: 'Microsoft 365 (work/school)',
    calIcs: 'Calendar file (.ics)',
    calIcsHint: 'One-time copy. It won’t update if your plans change.',
    notePh: 'Add a note, e.g. “Tennis with John, 9–10”',
    feedEnable: 'Sync to my calendar',
    feedHint:
      'Easiest option: set up once and every session you plan lands in your calendar automatically, updates included.',
    feedBusy: 'Setting up your calendar link…',
    feedError: 'Couldn’t reach the sync server. Check your connection and try again.',
    feedOn: 'Auto-sync is on. One last step: subscribe in your calendar.',
    feedAppleSteps:
      'Tap Apple Calendar below, then tap Subscribe when Calendar asks. That’s it, new sessions appear on their own.',
    feedApple: 'Apple Calendar',
    feedCopy: 'Copy link',
    feedCopied: '✓ Copied',
    feedCopyManual: 'Select and copy the link:',
    feedNote:
      'Changes, including deletions, update your private link within seconds. Apple Calendar re-checks it every few hours; Google and Outlook re-read it on their own schedule, which can take hours (sometimes up to a day), even after re-subscribing.',
    feedOff: 'Turn off auto-sync',
    notifyMe: 'Alert me if the forecast changes',
    notifyDenied: 'Notifications are blocked in your browser settings.',
    notifyUnsupported: 'Push alerts need this app installed to your home screen (iOS) or a supported browser.',
    planned: 'planned',
  },

  alerts: {
    title: 'Alerts',
    empty: 'No alerts. Plan a session and BlockCast will watch the forecast for you.',
    improved: '{activity} {when} improved: now {band}',
    worsened: '{activity} {when} worsened: now {band}',
    markRead: 'Mark all read',
    enablePush: 'Enable push alerts',
    pushOn: 'Push alerts on',
    pushOff: 'Turn off',
  },

  location: {
    set: 'Set your location',
    searchPh: 'Search a city or spot… e.g. Geneva, Le Valinouët, Lake Placid',
    kinds: {
      resort: 'Resort',
      ski: 'Ski area',
      peak: 'Peak',
      lake: 'Lake',
      river: 'River',
      falls: 'Falls',
      beach: 'Beach',
      bay: 'Bay',
      island: 'Island',
      park: 'Park / reserve',
      golf: 'Golf',
      marina: 'Marina',
      camp: 'Campground',
      hut: 'Hut',
      viewpoint: 'Viewpoint',
      forest: 'Forest',
      pass: 'Mountain pass',
    },
    mapHint:
      '…or tap the map to pick a precise spot: a trail head, a lake, a mountain. Drag the pin to fine-tune.',
    namePh: 'Name this spot',
    naming: 'Naming spot…',
    useSpot: 'Use this spot',
    noMatch: 'No matches.',
    myLoc: 'My location',
    mapUnavail: 'Map unavailable (couldn’t load Leaflet). Use the search above.',
    useMyLoc: 'Use my current position',
    mySpots: 'My spots',
    saveSpot: 'Save',
    change: 'Change location…',
    following: 'Following your device',
    pinned: 'Pinned spot',
    locating: 'Locating…',
    denied: 'Location is off for BlockCast. Turn it on in your device settings.',
    unavailable: 'Couldn’t get your position. Tap to try again.',
    lastUsed: 'Last used',
    layerSat: 'Satellite',
    layerMap: 'Map',
  },

  settings: {
    title: 'Settings',
    appearance: 'Appearance',
    theme: 'Theme',
    themeSystem: 'Auto',
    themeLight: 'Light',
    themeDark: 'Dark',
    language: 'Language',
    unitsTitle: 'Units',
    units: 'Measurements',
    metric: 'Metric (°C, km/h)',
    imperial: 'Imperial (°F, mph)',
    clock: 'Clock',
    about: 'About',
    aboutBody:
      'BlockCast scores every block of your week for your sport: green means go. Live data from Open-Meteo; radar by ECCC MSC; webcams by Windy.com; maps by MapLibre, Leaflet, OpenFreeMap & OpenStreetMap contributors. Settings and plans stay on your device.',
    version: 'Version',
    privacy: 'Privacy policy',
    getApp: 'Get the iPhone app',
  },

  intro:
    'BlockCast scores every block of your week for your sport: green means go. Pick your activity, tap a green block, plan your session.',

  activities: {
    tennis: 'Tennis',
    cycling: 'Cycling',
    jogging: 'Jogging',
    fishing: 'Fishing',
    golf: 'Golf',
    hiking: 'Hiking',
    sailing: 'Sailing',
    picnic: 'Picnic / BBQ',
    skiing: 'Skiing',
    snowmob: 'Snowmobiling',
    motorcycle: 'Motorcycle',
    utv: 'UTV / ATV',
    waterski: 'Waterskiing',
    pontoon: 'Pontooning',
    pickleball: 'Pickleball',
    beach: 'Hitting the Beach',
  },

  cats: {
    powersports: 'Motorsports',
    trail: 'Trail & Road',
    water: 'On the Water',
    court: 'Court & Course',
    snow: 'Snow',
    leisure: 'Leisure',
  },
};

export type Dict = typeof en;
